import {
  ESdChangeReason,
  IDisplayItem,
  IDisplayItemProperty,
  IKuikaStyle,
  IProperty,
  IScreenDesignDictionary,
  type IStylingEffects
} from "@kuika/kuika-cl-model";
import {
  applyEffectPreset,
  applyEffectPresetFill
} from "../../components/design-tools/right-panel/styling-panel/effects-presets/effects-presets-apply";
import { EFFECTS_PRESETS } from "../../components/design-tools/right-panel/styling-panel/effects-presets/effects-presets-data";
import type { EffectPreset } from "../../components/design-tools/right-panel/styling-panel/effects-presets/effects-presets-types";
import {
  defaultLoopingAnimationItem,
  type LoopingPreset,
  LOOPING_PRESETS
} from "../../components/design-tools/right-panel/styling-panel/looping-animation-helpers";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { applyPropertyGridChangeWithoutCommit } from "./designer-property-actions";
import { safeParse, getScreenDictionary as getScreenDesignDictionary } from "./designer-utils";
import {
  getComponentDef,
  applyCssStylesToComponent,
  canApplyStylesToStructuralWrapper,
  STATE_KEYS,
  type StyleState
} from "./mcp-tools/helpers/style-helpers";
import {
  BUILTIN_THEME_PALETTES,
  type ThemeColorPatch,
  themeColorPatchForType,
  type ThemePalette
} from "./mcp-tools/helpers/theme-restyle";
import { buildSkippedWarning } from "./mcp-tools/helpers/warning-helpers";
import {
  applyUnifiedStyleToStyleObj,
  findUnifiedStyleByGroupId,
  findUnifiedStyleByName,
  listProjectUnifiedStyles,
  type UnifiedStyleInfo
} from "./unified-style-helpers";

const STRUCTURAL_NAMES = new Set(["Row", "Col", "Column"]);
const ROOT_SECTION_INVISIBLE_STYLES = [
  "layout_width",
  "layout_maxWidth",
  "layout_display",
  "text_fontFamily",
  "text_fontWeight",
  "text_fontSize",
  "text_color",
  "text_lineHeight",
  "text_letterSpacing",
  "text_textDecoration",
  "text_textTransform"
];

/**
 * Component definition property shape from the component set.
 * This extends the base IDisplayItemProperty with extra fields
 * that exist at runtime in the component catalog but are not in
 * the persisted IDisplayItemProperty interface.
 */
type IComponentDefProperty = Omit<IProperty, "options"> & {
  options?: Array<string | { label?: string; name?: string; value: unknown }>;
};

export interface RootSectionDefinition {
  name: string;
  componentCategory: string;
  componentType: number;
  blockStyle: number;
  inVisibleStyles: string[];
  isContainer: boolean;
  availableEvents: string[];
  properties: IDisplayItemProperty[];
}

export const ROOT_SECTIONS: Record<string, RootSectionDefinition> = {
  "-1": {
    name: "Body",
    componentCategory: "Container",
    componentType: 10,
    blockStyle: 2,
    inVisibleStyles: ROOT_SECTION_INVISIBLE_STYLES,
    isContainer: true,
    availableEvents: ["pageInit", "Recurring"],
    properties: []
  },
  "-2": {
    name: "Header",
    componentCategory: "Container",
    componentType: 11,
    blockStyle: 2,
    inVisibleStyles: ROOT_SECTION_INVISIBLE_STYLES,
    isContainer: true,
    availableEvents: [],
    properties: []
  },
  "-3": {
    name: "Footer",
    componentCategory: "Container",
    componentType: 12,
    blockStyle: 2,
    inVisibleStyles: ROOT_SECTION_INVISIBLE_STYLES,
    isContainer: true,
    availableEvents: [],
    properties: []
  }
};

function getDisplayItemProvider(sd: IScreenDesignContextState): IScreenDesignContextState {
  return (
    (sd as IScreenDesignContextState & { displayItemProvider?: IScreenDesignContextState })?.displayItemProvider || sd
  );
}

function isStructuralWrapper(di: IDisplayItem, dict: IScreenDesignDictionary): boolean {
  if (!di || !di.item?.name || !STRUCTURAL_NAMES.has(di.item.name)) return false;
  if (di.item?.name === "Row" && di.parentId === -1) return true;
  if ((di.item?.name === "Col" || di.item?.name === "Column") && di.parentId != null) {
    const parentDi = dict[di.parentId];
    if (parentDi?.item?.name === "Row" && parentDi.parentId === -1) return true;
  }
  return false;
}

interface StateStylesInputShape {
  hover?: unknown;
  pressed?: unknown;
  focused?: unknown;
  disabled?: unknown;
}

interface BatchStyleComponent {
  kuikaId: string;
  styles: unknown;
  state?: StyleState;
  stateStyles?: StateStylesInputShape;
}

interface StylesInput {
  kuikaId?: string;
  styles?: unknown;
  state?: StyleState;
  /**
   * Optional per-state CSS bucket map. Each key — `hover` / `pressed` /
   * `focused` / `disabled` — carries a CSS-property → value object
   * applied in a separate pass with `options.state` so the state pipeline
   * (`liftSectionChangesToState`) routes the change into `{state}Styles`.
   * Works in both single and batch shapes. Empty buckets are skipped.
   *
   * If `state` AND `stateStyles[state]` are both supplied, the
   * `stateStyles[state]` bucket runs SECOND and silently overwrites whatever
   * the `state`+`styles` pass wrote — to avoid that surprise, the call is
   * rejected with an error.
   */
  stateStyles?: StateStylesInputShape;
  components?: BatchStyleComponent[];
}

export const VALID_STATES: ReadonlySet<StyleState> = new Set<StyleState>(["hover", "pressed", "focused", "disabled"]);

export function normalizeState(state: unknown): StyleState | undefined {
  if (typeof state !== "string") return undefined;
  if (state === "none" || state === "") return undefined;
  return VALID_STATES.has(state as StyleState) ? (state as StyleState) : undefined;
}

interface StylesResult {
  success: boolean;
  count: number;
  warnings?: string[];
}

interface BatchStylesResult {
  success: boolean;
  message: string;
  results: Array<StylesResult | StylesSkipped | StylesError>;
}

interface StylesSkipped {
  skipped: true;
  message: string;
}

interface StylesError {
  error: string;
}

function styleSingleComponent(
  sdInstance: ScreenDesignInstance,
  kuikaId: string,
  rawStyles: unknown,
  state?: StyleState
): StylesResult | StylesSkipped | StylesError {
  const styles = safeParse(rawStyles);
  if (!styles || typeof styles !== "object" || Object.keys(styles as Record<string, unknown>).length === 0) {
    return { error: `styles object is required for kuikaId "${kuikaId}"` };
  }

  // Resolve $N.M and @alias refs from the last add call's refMap
  let resolvedId = kuikaId;
  if (resolvedId.startsWith("$")) {
    const refMap: Record<string, string> = (window as any).kuika?._mcpLastRefMap || {};
    if (refMap[resolvedId]) resolvedId = refMap[resolvedId];
  } else if (resolvedId.startsWith("@")) {
    const aliasMap: Record<string, string> = (window as any).kuika?._mcpLastAliasMap || {};
    // Support @alias.N for auto-created children (e.g., "@_tab.0" → first TabItem)
    const aliasDotMatch = resolvedId.match(/^@([^.]+)\.(\d+)$/);
    if (aliasDotMatch) {
      const parentId = aliasMap[aliasDotMatch[1]];
      if (parentId) {
        // Defer child lookup to after dict is available (below)
        resolvedId = `__alias_child__${parentId}__${aliasDotMatch[2]}`;
      }
    } else if (aliasMap[resolvedId.slice(1)]) {
      resolvedId = aliasMap[resolvedId.slice(1)];
    }
  }

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);

  // Complete @alias.N resolution now that dict is available
  if (resolvedId.startsWith("__alias_child__")) {
    const parts = resolvedId.split("__");
    const parentId = parts[2];
    const childIdx = parseInt(parts[3], 10);
    const parentDi = dict[parseInt(parentId, 10)];
    if (parentDi?.children && parentDi.children[childIdx]) {
      resolvedId = parentDi.children[childIdx].id?.toString();
    }
  }
  const di = dict[parseInt(resolvedId, 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };

  if (isStructuralWrapper(di, dict) && !canApplyStylesToStructuralWrapper(styles)) {
    return {
      skipped: true,
      message: `Skipped styling ${di.item?.name} (${kuikaId}) — it is a structural wrapper element (Row/Col). Only style the actual content component inside it.`
    };
  }

  const result = applyCssStylesToComponent(
    sdInstance,
    di,
    kuikaId,
    styles as Record<string, unknown>,
    state ? { state } : undefined
  );
  const componentName = di.item?.name || "Unknown";
  const warnings =
    result.skippedProperties.length > 0
      ? result.skippedProperties.map((property: string) => buildSkippedWarning(property, componentName))
      : undefined;

  return {
    success: result.success,
    count: result.appliedProperties.length,
    ...(warnings && { warnings })
  };
}

function expandStateStyles(
  sdInstance: ScreenDesignInstance,
  kuikaId: string,
  stateStyles: StateStylesInputShape | undefined
): Array<StylesResult | StylesSkipped | StylesError> {
  if (!stateStyles || typeof stateStyles !== "object") return [];
  const out: Array<StylesResult | StylesSkipped | StylesError> = [];
  for (const state of STATE_KEYS) {
    const bucket = (stateStyles as Record<StyleState, unknown>)[state];
    if (bucket === undefined || bucket === null) continue;
    if (typeof bucket !== "object" || Array.isArray(bucket)) {
      out.push({
        error: `stateStyles.${state} must be a CSS-property object (got ${Array.isArray(bucket) ? "array" : typeof bucket})`
      });
      continue;
    }
    if (Object.keys(bucket as Record<string, unknown>).length === 0) continue;
    out.push(styleSingleComponent(sdInstance, kuikaId, bucket, state));
  }
  return out;
}

/**
 * Rejects the request when `state` (single-state retarget for the top-level
 * `styles` payload) and `stateStyles[state]` (per-state map) both target the
 * same state with non-empty content. Without this guard the two passes both
 * write into `{state}Styles` and the second (`stateStyles`) silently
 * overwrites the first, which surprises hand-rolled callers. Returns the
 * conflicting state name on detection, `null` otherwise.
 */
function detectStateOverlap(
  state: StyleState | undefined,
  stateStyles: StateStylesInputShape | undefined
): StyleState | null {
  if (!state || !stateStyles || typeof stateStyles !== "object") return null;
  const bucket = (stateStyles as Record<StyleState, unknown>)[state];
  if (bucket && typeof bucket === "object" && !Array.isArray(bucket) && Object.keys(bucket as object).length > 0) {
    return state;
  }
  return null;
}

export function setDesignerComponentStyles(
  sdInstance: ScreenDesignInstance,
  input: StylesInput
): StylesResult | StylesSkipped | StylesError | BatchStylesResult {
  const batchState = normalizeState(input.state);

  // Batch mode: components array
  if (input.components && Array.isArray(input.components) && input.components.length > 0) {
    const results: Array<StylesResult | StylesSkipped | StylesError> = [];
    for (const item of input.components) {
      if (!item.kuikaId) {
        results.push({ error: "kuikaId is required for each component in batch" });
        continue;
      }
      const perItemState = normalizeState(item.state) ?? batchState;
      const overlap = detectStateOverlap(perItemState, item.stateStyles);
      if (overlap) {
        results.push({
          error: `kuikaId "${item.kuikaId}": both state="${overlap}" and stateStyles.${overlap} were supplied — pick one (state retargets the top-level styles payload, stateStyles is a separate per-state map)`
        });
        continue;
      }
      const hasBase = item.styles && typeof item.styles === "object" && Object.keys(item.styles as object).length > 0;
      if (hasBase) {
        results.push(styleSingleComponent(sdInstance, item.kuikaId, item.styles, perItemState));
      }
      results.push(...expandStateStyles(sdInstance, item.kuikaId, item.stateStyles));
    }
    const successCount = results.filter((r) => "success" in r && r.success).length;
    const skippedCount = results.filter((r) => "skipped" in r).length;
    const errorCount = results.filter((r) => "error" in r).length;
    return {
      success: errorCount === 0,
      message: `Batch styled ${input.components.length} components: ${successCount} succeeded, ${skippedCount} skipped, ${errorCount} errors`,
      results
    };
  }

  // Single mode: kuikaId + styles (+ optional stateStyles map)
  const { kuikaId } = input;
  if (!kuikaId) return { error: "kuikaId is required (or use 'components' array for batch)" };
  const overlap = detectStateOverlap(batchState, input.stateStyles);
  if (overlap) {
    return {
      error: `kuikaId "${kuikaId}": both state="${overlap}" and stateStyles.${overlap} were supplied — pick one (state retargets the top-level styles payload, stateStyles is a separate per-state map)`
    };
  }
  const hasBase = input.styles && typeof input.styles === "object" && Object.keys(input.styles as object).length > 0;
  const stateExpansion = expandStateStyles(sdInstance, kuikaId, input.stateStyles);
  if (stateExpansion.length === 0) {
    return styleSingleComponent(sdInstance, kuikaId, input.styles, batchState);
  }
  const results: Array<StylesResult | StylesSkipped | StylesError> = [];
  if (hasBase) results.push(styleSingleComponent(sdInstance, kuikaId, input.styles, batchState));
  results.push(...stateExpansion);
  const successCount = results.filter((r) => "success" in r && r.success).length;
  const skippedCount = results.filter((r) => "skipped" in r).length;
  const errorCount = results.filter((r) => "error" in r).length;
  return {
    success: errorCount === 0,
    message: `Styled 1 component across ${results.length} layer(s): ${successCount} succeeded, ${skippedCount} skipped, ${errorCount} errors`,
    results
  };
}

interface ComponentDefinitionResult {
  name: string;
  componentCategory: string | null;
  componentType: number | null;
  blockStyle: number | boolean;
  inVisibleStyles: string[];
  availableEvents: string[];
  properties: Array<{
    name: string;
    type: number;
    fixedValue?: unknown;
    hasOptions: boolean;
    options?: Array<{ label: string; value: unknown }>;
  }>;
}

interface ComponentDefinitionError {
  error: string;
  componentName?: string;
}

export function getDesignerComponentDefinition(
  sdInstance: ScreenDesignInstance,
  input: { kuikaId?: string }
): RootSectionDefinition | ComponentDefinitionResult | ComponentDefinitionError {
  const { kuikaId } = input;
  if (kuikaId && ROOT_SECTIONS[kuikaId]) {
    return ROOT_SECTIONS[kuikaId];
  }

  const sd = getValues(sdInstance);
  let di: IDisplayItem | undefined;
  if (kuikaId) {
    const dict = getScreenDesignDictionary(sd);
    di = dict[parseInt(kuikaId, 10)];
    if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };
  } else {
    di = getDisplayItemProvider(sd)?.getSelectedDisplayItem?.(sd.getScreenDesign());
    if (!di) return { error: "No kuikaId provided and no component is selected" };
  }

  const componentDef = getComponentDef(sdInstance, di);
  if (!componentDef) {
    return {
      error: `Component definition not found for "${di.item?.name}"`,
      componentName: di.item?.name
    };
  }

  const componentDefProperties: IComponentDefProperty[] = componentDef.properties || [];

  const availableEvents = componentDefProperties
    .filter((property) => property.selectedType === 3)
    .map((property) => property.name);

  const properties = componentDefProperties
    .filter((property) => property.selectedType !== 3 && !property.isTechnical && !property.isDeprecated)
    .map((property) => {
      const result: {
        name: string;
        type: number;
        fixedValue?: unknown;
        hasOptions: boolean;
        options?: Array<{ label: string; value: unknown }>;
      } = {
        name: property.name,
        type: property.selectedType
      } as {
        name: string;
        type: number;
        fixedValue?: unknown;
        hasOptions: boolean;
        options?: Array<{ label: string; value: unknown }>;
      };

      if (property.symbol?.fixedValue !== undefined) {
        result.fixedValue = property.symbol.fixedValue;
      }

      if (property.options && property.options.length > 0) {
        result.hasOptions = true;
        result.options = property.options.map((option) => ({
          label: typeof option === "string" ? option : option.label || option.name || "",
          value: typeof option === "string" ? option : option.value
        }));
      } else {
        result.hasOptions = false;
      }

      return result;
    });

  return {
    name: componentDef.name,
    componentCategory: componentDef.componentCategory || null,
    componentType: componentDef.componentType || null,
    blockStyle: componentDef.blockStyle || false,
    inVisibleStyles: componentDef.inVisibleStyles || [],
    availableEvents,
    properties
  };
}

interface ListUnifiedStylesResult {
  success: true;
  unifiedStyles: UnifiedStyleInfo[];
}

interface UnifiedOperationError {
  error: string;
}

/**
 * MCP handler — list every project-level unified style token across all 5
 * styling collections. Read-only; the lookup mirrors the dropdown the
 * styling panel exposes (`getUnifiedStyleList` in
 * unified-styling-selector.tsx) but returns the lighter `UnifiedStyleInfo`
 * shape that's friendlier for tool consumers. Legacy single-section tokens
 * (no groupId) are intentionally omitted — `apply_unified` only operates
 * on true 5-section tokens, so the list reflects what's applicable.
 */
export function listProjectUnifiedStylesForMcp(sdInstance: ScreenDesignInstance): ListUnifiedStylesResult {
  const project = sdInstance.projectProvider.getProject();
  if (!project) return { success: true, unifiedStyles: [] };
  return { success: true, unifiedStyles: listProjectUnifiedStyles(project) };
}

interface ApplyUnifiedInput {
  kuikaId?: string;
  groupId?: string;
  name?: string;
}

interface ApplyUnifiedResult {
  success: boolean;
  kuikaId: string;
  groupId: string;
  appliedSections: string[];
  message: string;
}

/**
 * MCP handler — attach an existing unified style token to a component by
 * extending its per-section `*Ids` arrays with the token's section IDs.
 * Idempotent: applying the same token twice is a no-op on already-attached
 * IDs (dedup in `applyUnifiedStyleToStyleObj`).
 *
 * Looks up the token by `groupId` first; falls back to `name`. CREATE /
 * DELETE for unified tokens stays in the styling panel UI by design.
 *
 * Commits via the `ChangeStyleValue` reason on the `styleObj` property,
 * matching `applyCssStylesToComponent`'s commit path so canvas + property
 * panel both refresh after the call.
 */
interface ThemeInput {
  theme?: string;
  palette?: Partial<ThemePalette>;
}

interface ThemeResult {
  success: boolean;
  themed: number;
  total: number;
  message: string;
}

interface ThemeError {
  error: string;
}

/**
 * Deterministic whole-screen re-theme (restyle_target). Walks EVERY component in
 * the screen dictionary, maps its type → semantic role → color patch, and writes
 * ONLY color fields (fill/text/border color) onto its styleObj — layout, spacing
 * and size are never touched. Coverage is guaranteed (unknown types get a safe
 * text-color default), so a 38-component screen is fully re-themed in ONE commit
 * — no LLM enumeration, no completeness loop. Returns the read-back count
 * (themed / total) as the "did every component get themed" evidence.
 */
export function applyThemeToScreen(sdInstance: ScreenDesignInstance, input: ThemeInput): ThemeResult | ThemeError {
  let palette: ThemePalette | undefined;
  const themeKey: string = (input.theme ?? "").trim().toLowerCase();
  if (themeKey === "dark" || themeKey === "light") {
    palette = BUILTIN_THEME_PALETTES[themeKey];
  }
  if (input.palette) {
    // Custom palette overrides / fills in on top of a builtin base (or dark default).
    const base: ThemePalette = palette ?? BUILTIN_THEME_PALETTES.dark;
    palette = { ...base, ...input.palette };
  }
  if (!palette) {
    return { error: 'Provide theme:"dark"|"light" or a palette object with the role colors.' };
  }

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const dict: IScreenDesignDictionary = getScreenDesignDictionary(sd);
  let themed = 0;
  let total = 0;

  for (const key of Object.keys(dict)) {
    const di: IDisplayItem | undefined = dict[Number(key)];
    if (!di) continue;
    // The body root (-1) and header/footer (-2/-3) have NO item.name ("Unknown"),
    // so a `!componentType` skip drops the PAGE BACKGROUND — the most visible
    // surface. Default the type to "" and let themeColorPatchForType key roots by
    // id (ROOT_BACKGROUND_IDS / ROOT_SURFACE_IDS).
    const componentType: string = di.item?.name ?? "";
    total += 1;
    const patch: ThemeColorPatch = themeColorPatchForType(componentType, palette, key);
    if (patch.backgroundColor === undefined && patch.textColor === undefined && patch.borderColor === undefined) {
      continue;
    }
    const styleObj: IKuikaStyle = JSON.parse(JSON.stringify(di.styleObj || {})) as IKuikaStyle;
    if (patch.backgroundColor !== undefined) {
      if (!styleObj.fill) styleObj.fill = { fillId: null, fillIds: null, kuikaid: key };
      styleObj.fill.color = patch.backgroundColor;
    }
    if (patch.textColor !== undefined) {
      if (!styleObj.text) styleObj.text = { typoId: null, typoIds: null, kuikaid: key };
      styleObj.text.color = patch.textColor;
    }
    if (patch.borderColor !== undefined) {
      if (!styleObj.border) styleObj.border = { borderId: null, kuikaid: key };
      styleObj.border.borderColor = patch.borderColor;
    }
    applyPropertyGridChangeWithoutCommit(
      sd,
      { propertyName: "styleObj", value: styleObj, kuikaId: key },
      ESdChangeReason.ChangeStyleValue
    );
    themed += 1;
  }

  if (themed > 0) {
    sdInstance.screenProvider.setScreenDesign(sdInstance.screenProvider.getScreenDesign(), 1);
  }

  return {
    success: themed > 0,
    themed,
    total,
    message: `Re-themed ${themed}/${total} component(s).`
  };
}

interface CopyStylePair {
  fromKuikaId: string;
  toKuikaId: string;
}

interface CopyStylesInput {
  pairs?: CopyStylePair[];
  fromKuikaId?: string;
  toKuikaId?: string;
}

interface CopyStylePairResult {
  fromKuikaId: string;
  toKuikaId: string;
  success: boolean;
  error?: string;
}

interface CopyStylesResult {
  success: boolean;
  copied: number;
  results: CopyStylePairResult[];
  message: string;
}

interface CopyStylesError {
  error: string;
}

/**
 * Plan B lossless redesign (primitive 2b) — copy a source
 * component's FULL visual `styleObj` (every section incl. state styles,
 * effects, and the `styleGroupId`/`styleGroupName` unified-style link) onto one
 * or more target components, PRESERVING each target's canvas position coords
 * (`position.xAxis`/`yAxis`). This is the `pasteItemStyle` contract
 * (`drag-drop-helper.ts`) minus the clipboard and minus the same-component-type
 * gate — the Plan B reconcile already friendlyName+type-matches each pair, and a
 * `styleObj` is componentType-agnostic CSS.
 *
 * Only `styleObj` is read and written; nothing on `item` (events / validations /
 * bound properties) is touched — so pushing the redesigned look onto a moved
 * live node (or its subtree) can never disturb its wiring. Best-effort per pair;
 * a missing source/target is reported, not thrown.
 */
export function copyComponentStyles(
  sdInstance: ScreenDesignInstance,
  input: CopyStylesInput
): CopyStylesResult | CopyStylesError {
  const pairs: CopyStylePair[] =
    input.pairs && input.pairs.length > 0
      ? input.pairs
      : input.fromKuikaId && input.toKuikaId
        ? [{ fromKuikaId: input.fromKuikaId, toKuikaId: input.toKuikaId }]
        : [];
  if (pairs.length === 0) {
    return { error: "Provide pairs:[{fromKuikaId,toKuikaId}] or a single fromKuikaId + toKuikaId." };
  }

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const dict: IScreenDesignDictionary = getScreenDesignDictionary(sd);
  const results: CopyStylePairResult[] = [];
  let anyApplied = false;

  for (const pair of pairs) {
    const fromId: string = pair.fromKuikaId != null ? String(pair.fromKuikaId) : "";
    const toId: string = pair.toKuikaId != null ? String(pair.toKuikaId) : "";
    if (!fromId || !toId) {
      results.push({
        fromKuikaId: fromId,
        toKuikaId: toId,
        success: false,
        error: "fromKuikaId and toKuikaId are required"
      });
      continue;
    }
    const fromDi: IDisplayItem | undefined = dict[parseInt(fromId, 10)];
    const toDi: IDisplayItem | undefined = dict[parseInt(toId, 10)];
    if (!fromDi) {
      results.push({ fromKuikaId: fromId, toKuikaId: toId, success: false, error: `Source "${fromId}" not found` });
      continue;
    }
    if (!toDi) {
      results.push({ fromKuikaId: fromId, toKuikaId: toId, success: false, error: `Target "${toId}" not found` });
      continue;
    }

    const sourceStyle: IKuikaStyle = JSON.parse(JSON.stringify(fromDi.styleObj || {})) as IKuikaStyle;
    const targetStyle: IKuikaStyle = JSON.parse(JSON.stringify(toDi.styleObj || {})) as IKuikaStyle;
    // Source wins on every section (adopt the new look wholesale); then restore
    // the target's canvas position coords so a moved node keeps its own spot.
    const merged: IKuikaStyle = { ...targetStyle, ...sourceStyle };
    if (targetStyle.position && merged.position) {
      merged.position = { ...merged.position, xAxis: targetStyle.position.xAxis, yAxis: targetStyle.position.yAxis };
    }
    applyPropertyGridChangeWithoutCommit(
      sd,
      { propertyName: "styleObj", value: merged, kuikaId: toId },
      ESdChangeReason.ChangeStyleValue
    );
    anyApplied = true;
    results.push({ fromKuikaId: fromId, toKuikaId: toId, success: true });
  }

  if (anyApplied) {
    sdInstance.screenProvider.setScreenDesign(sdInstance.screenProvider.getScreenDesign(), 1);
  }

  const copied: number = results.filter((r: CopyStylePairResult): boolean => r.success).length;
  return {
    success: copied > 0,
    copied,
    results,
    message: `Copied styling for ${copied}/${pairs.length} component(s).`
  };
}

export function applyUnifiedStyleToComponent(
  sdInstance: ScreenDesignInstance,
  input: ApplyUnifiedInput
): ApplyUnifiedResult | UnifiedOperationError {
  const { kuikaId, groupId, name } = input;
  if (!kuikaId) return { error: "kuikaId is required" };
  if (!groupId && !name) return { error: "Either groupId or name is required" };

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };
  if (isStructuralWrapper(di, dict)) {
    return {
      error: `Cannot attach unified style to structural wrapper (${di.item?.name}) — apply to the inner component instead.`
    };
  }

  const project = sdInstance.projectProvider.getProject();
  if (!project) return { error: "Project not loaded" };

  const entries = groupId
    ? findUnifiedStyleByGroupId(project, groupId)
    : findUnifiedStyleByName(project, name as string);
  if (!entries) {
    return {
      error: groupId
        ? `Unified style with groupId "${groupId}" not found`
        : `Unified style with name "${name}" not found`
    };
  }

  // Deep-clone so we don't mutate the live DI before commit — matches the
  // conservative snapshot → apply → commit pattern in applyCssStylesToComponent.
  const styleObj = JSON.parse(JSON.stringify(di.styleObj || {})) as IKuikaStyle;
  const touched = applyUnifiedStyleToStyleObj(styleObj, entries);
  if (touched.length === 0) {
    return {
      success: true,
      kuikaId,
      groupId: entries.groupId,
      appliedSections: [],
      message: `Unified style "${entries.name || entries.groupId}" is already attached to component ${kuikaId}.`
    };
  }

  applyPropertyGridChangeWithoutCommit(
    sd,
    { propertyName: "styleObj", value: styleObj, kuikaId },
    ESdChangeReason.ChangeStyleValue
  );
  sdInstance.screenProvider.setScreenDesign(sdInstance.screenProvider.getScreenDesign(), 1);

  return {
    success: true,
    kuikaId,
    groupId: entries.groupId,
    appliedSections: touched,
    message: `Attached unified style "${entries.name || entries.groupId}" to ${di.item?.name ?? "component"} ${kuikaId} (${touched.join(", ")}).`
  };
}

interface ApplyEffectPresetInput {
  kuikaId?: string;
  preset?: string;
  state?: unknown;
}

interface ApplyEffectPresetResult {
  success: true;
  kuikaId: string;
  preset: string;
  appliedTo: "base" | StyleState;
  message: string;
}

interface EffectPresetError {
  error: string;
}

/**
 * Strip identity + persisted-state-bucket + entry-animation keys from a fresh
 * `applyEffectPreset` result, leaving only the effect fields the preset
 * actually wrote (opacity / transforms / transitions / filters / animations /
 * …). Used when a preset targets a {state} bucket: those fields are merged
 * into `effects.{state}Styles` instead of base. `entryAnimation` is excluded
 * because it is base-only by design (mount animations don't fire on state).
 */
function extractEffectDelta(applied: IStylingEffects): Partial<IStylingEffects> {
  const {
    effectsId: _effectsId,
    effectsIds: _effectsIds,
    kuikaid: _kuikaid,
    hoverStyles: _hoverStyles,
    pressedStyles: _pressedStyles,
    focusedStyles: _focusedStyles,
    disabledStyles: _disabledStyles,
    entryAnimation: _entryAnimation,
    ...delta
  } = applied;
  return delta;
}

/**
 * Apply a ready-made effect preset (looping/entry animation or a multi-effect
 * bundle) to a component by name. Animations cannot ride a CSS `style=` string
 * (`@keyframes` can't be expressed inline), so this is the MCP/AI channel for
 * them — reusing the same pure `applyEffectPreset` the designer panel runs.
 *
 * `preset` accepts an EFFECTS_PRESETS id (e.g. "hover-lift", "fade-in",
 * "pressed-depth") or a looping key (pulse / float / spin / bounce / shake /
 * glow / ping / shimmer / heartbeat). The target state is the explicit `state`
 * param when given, else the preset's own `targetState`. Web-only: native
 * generators never read styleObj.effects.
 */
export function applyEffectPresetToComponent(
  sdInstance: ScreenDesignInstance,
  input: ApplyEffectPresetInput
): ApplyEffectPresetResult | EffectPresetError {
  const kuikaId: string | undefined = input.kuikaId;
  const presetId: string | undefined = typeof input.preset === "string" ? input.preset.trim() : undefined;
  if (!kuikaId) return { error: "kuikaId is required" };
  if (!presetId) return { error: "preset is required (an effect preset id or a looping key)" };

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };
  if (isStructuralWrapper(di, dict)) {
    return {
      error: `Cannot apply an effect preset to structural wrapper (${di.item?.name}) — target the inner component instead.`
    };
  }

  const lowerId: string = presetId.toLowerCase();
  const preset: EffectPreset | undefined = EFFECTS_PRESETS.find(
    (p: EffectPreset): boolean => p.id.toLowerCase() === lowerId
  );
  const loopingPreset: LoopingPreset | undefined = LOOPING_PRESETS.find(
    (p: LoopingPreset): boolean => p.key.toLowerCase() === lowerId
  );
  if (!preset && !loopingPreset) {
    const sampleIds: string = EFFECTS_PRESETS.slice(0, 4)
      .map((p: EffectPreset): string => p.id)
      .join(", ");
    const loopKeys: string = LOOPING_PRESETS.map((p: LoopingPreset): string => p.key).join(", ");
    return {
      error: `Unknown preset "${presetId}". Use an effect preset id (e.g. ${sampleIds}, …) or a looping key (${loopKeys}).`
    };
  }

  // Explicit state override wins; otherwise honor the preset's own target.
  const explicitState: StyleState | undefined = normalizeState(input.state);
  let targetState: "base" | StyleState = "base";
  if (explicitState) {
    targetState = explicitState;
  } else if (preset && preset.targetState !== "base") {
    targetState = preset.targetState;
  }

  const styleObj = JSON.parse(JSON.stringify(di.styleObj || {})) as IKuikaStyle;

  if (targetState === "base") {
    if (preset) {
      styleObj.effects = applyEffectPreset(styleObj.effects, preset, { kuikaid: kuikaId });
      const nextFill = applyEffectPresetFill(styleObj.fill, preset, kuikaId);
      if (nextFill) {
        styleObj.fill = nextFill;
      }
    } else {
      const eff: IStylingEffects = styleObj.effects ?? { effectsId: null, kuikaid: kuikaId };
      eff.animations = [defaultLoopingAnimationItem(loopingPreset!.key)];
      styleObj.effects = eff;
    }
  } else {
    // State target: write only the preset's touched fields into {state}Styles,
    // leaving base intact (mirrors the panel's saveFormDataToStateStyles route).
    const stateKey = `${targetState}Styles`;
    const delta: Partial<IStylingEffects> = preset
      ? extractEffectDelta(applyEffectPreset({ effectsId: null, kuikaid: kuikaId }, preset, { kuikaid: kuikaId }))
      : { animations: [defaultLoopingAnimationItem(loopingPreset!.key)] };
    const eff = (styleObj.effects ?? { effectsId: null, kuikaid: kuikaId }) as IStylingEffects &
      Record<string, unknown>;
    const existing = (eff[stateKey] as Record<string, unknown> | undefined) ?? {};
    eff[stateKey] = { ...existing, ...delta };
    styleObj.effects = eff;
  }

  applyPropertyGridChangeWithoutCommit(
    sd,
    { propertyName: "styleObj", value: styleObj, kuikaId },
    ESdChangeReason.ChangeStyleValue
  );
  sdInstance.screenProvider.setScreenDesign(sdInstance.screenProvider.getScreenDesign(), 1);

  const label: string = preset ? preset.name : loopingPreset!.label;
  const stateSuffix: string = targetState === "base" ? "" : ` (${targetState} state)`;
  return {
    success: true,
    kuikaId,
    preset: presetId,
    appliedTo: targetState,
    message: `Applied effect preset "${label}" to ${di.item?.name ?? "component"} ${kuikaId}${stateSuffix}.`
  };
}
